import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { useIntl } from 'ha/i18n';
import { Container, Typography, NoSSR, units } from '@hbf/dsl';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { LoadableSwiperSlider } from 'ha/components/SwiperSlider';
import { CityCard } from 'ha/components/CityCard';

import { TopCity, CityListingsCountsMap } from '../../types';
import { SliderControl } from './SliderControl';

const useStyles = makeStyles()(theme => ({
  wrapper: {
    padding: units.yx(3.375, 0),
    backgroundColor: '#f8f9fa',
    [theme.breakpoints.up('md')]: {
      padding: units.txb(3, 0, 5),
    },
  },
  title: {
    fontSize: units.rem(1.5),
    marginBottom: units.rem(0.75),
    [theme.breakpoints.up('md')]: {
      fontSize: units.rem(1.875),
      marginBottom: units.rem(1.25),
    },
  },
  subtitle: {
    fontWeight: 'normal',
    fontSize: units.rem(1),
    marginBottom: units.rem(1.5),
    [theme.breakpoints.up('md')]: {
      marginBottom: units.rem(2),
    },
  },
  container: {
    position: 'relative',
  },
  swiperWrapper: {
    padding: units.yx(0, 5),
    [theme.breakpoints.down('md')]: {
      padding: 0,
    },
  },
  controls: {
    position: 'absolute',
    top: 170,
  },
  leftControl: {
    marginRight: units.rem(2),
    left: 0,
  },
  rightControl: {
    marginLeft: units.rem(2),
    right: 0,
  },
}));

interface Props {
  list: TopCity[];
  listingsCounts?: CityListingsCountsMap;
}

const TopCities: React.FC<Props> = ({ list, listingsCounts }) => {
  const { T, lang } = useIntl();
  const { classes, cx } = useStyles();
  const { md, sm } = useMediaQuery();

  const cards = React.useMemo(
    () =>
      list.map(content => ({
        key: content.name,
        element: (
          <CityCard
            testLocator={`Home TopCities CityCard ${content.name}`}
            listingsCount={listingsCounts && listingsCounts[content.name]}
            url={content.urls[lang]}
            image={content.image}
            name={T(content.name)}
            description={T(content.description)}
          />
        ),
      })),
    [T, list, lang, listingsCounts],
  );

  const slidesPerView = React.useMemo(() => {
    if (md) {
      return 4.2;
    }
    if (sm) {
      return 2.2;
    }
    return 1.1;
  }, [md, sm]);

  const SliderPrev = (props: object) => (
    <React.Fragment>
      {md && (
        <div className={cx(classes.controls, classes.leftControl)}>
          <SliderControl
            {...props}
            data-test-locator="Home TopCities Left Control"
            direction="left"
          />
        </div>
      )}
    </React.Fragment>
  );

  const SliderNext = (props: object) => (
    <React.Fragment>
      {md && (
        <div className={cx(classes.controls, classes.rightControl)}>
          <SliderControl
            {...props}
            data-test-locator="Home TopCities Right Control"
            direction="right"
          />
        </div>
      )}
    </React.Fragment>
  );

  return (
    <div className={classes.wrapper}>
      <Container maxWidth="lg">
        <Typography.H2
          component="h2"
          align="center"
          className={classes.title}
          data-test-locator="Home TopCities Title"
        >
          {T('home_page_top_cities_title')}
        </Typography.H2>
        <Typography.H4
          color="muted"
          align="center"
          className={classes.subtitle}
          data-test-locator="Home TopCities Subtitle"
          component="h3"
        >
          {T('home_page_top_cities_subtitle')}
        </Typography.H4>

        <NoSSR>
          <div className={classes.container}>
            <div className={classes.swiperWrapper}>
              <LoadableSwiperSlider
                items={cards}
                spaceBetween={16}
                slidesPerView={slidesPerView}
                swiperContainerStyles={{
                  // needed for scale animation on hover
                  paddingTop: units.rem(2),
                  paddingBottom: units.rem(2),
                }}
                withKeyboardControls
                loop
                controls={[SliderPrev, SliderNext]}
              />
            </div>
          </div>
        </NoSSR>
      </Container>
    </div>
  );
};

export { TopCities };
